import { useState } from "react";

interface UseFileDurationReturn {
  getDuration: (file: File) => Promise<number>;
  isChecking: boolean;
}

/**
 * Медиадвижок может не ответить вовсе — расширение-блокировщик или отключённые
 * кодеки оставляют элемент в networkState=LOADING без loadedmetadata и без
 * error. Без ограничения по времени промис висит вечно, и форма загрузки
 * навсегда застревает на «Определение длительности файла…».
 */
const METADATA_TIMEOUT_MS = 8000;

export const useFileDuration = (): UseFileDurationReturn => {
  const [isChecking, setIsChecking] = useState(false);

  const getDuration = async (file: File): Promise<number> => {
    setIsChecking(true);

    try {
      return await new Promise<number>((resolve, reject) => {
        const video = document.createElement("video");
        video.preload = "metadata";
        const objectUrl = URL.createObjectURL(file);

        const timeoutId = window.setTimeout(() => {
          cleanup();
          reject(new Error("Не удалось определить длительность файла: таймаут чтения метаданных"));
        }, METADATA_TIMEOUT_MS);

        const cleanup = (): void => {
          window.clearTimeout(timeoutId);
          video.onloadedmetadata = null;
          video.onerror = null;
          video.removeAttribute("src");
          video.load();
          window.URL.revokeObjectURL(objectUrl);
        };

        video.onloadedmetadata = (): void => {
          const durationMinutes = Math.ceil(video.duration / 60);
          cleanup();
          resolve(durationMinutes);
        };

        video.onerror = (): void => {
          cleanup();
          reject(new Error("Не удалось прочитать метаданные файла"));
        };

        video.src = objectUrl;
      });
    } finally {
      setIsChecking(false);
    }
  };

  return {
    getDuration,
    isChecking
  };
};
